/**
 * Sprint A · Historia A1 — Schema Zod del consolidado de una auditoría
 * cerrada. Lo consume el frontend en la vista `/assessments/:id/closed`
 * (tab Resumen).
 *
 * Fuente del payload: GET /api/v1/assessments/:id/summary
 * (apps/api/src/assessments/routes.ts).
 *
 * El heatmap por dominio del tech-design quedó fuera del Sprint A: la
 * extracción de la función compartida con el worker pdf.ts es trabajo
 * de un sprint posterior. Por ahora el resumen entrega conteos por
 * severidad/clasificación/status + top 5 findings + evidence summary.
 */
import { z } from 'zod';

export const SEVERITY_VALUES = ['critical', 'high', 'medium', 'low', 'info'] as const;
export const CLASSIFICATION_VALUES = ['nc_major', 'nc_minor', 'observation', 'ofi'] as const;
// Status real persistido en la tabla findings (CHECK SQL en schema.prisma):
//   'open' | 'in_progress' | 'resolved' | 'closed' | 'dismissed'.
export const FINDING_STATUS_VALUES = [
  'open',
  'in_progress',
  'resolved',
  'closed',
  'dismissed',
] as const;

export const AssessmentSummarySchema = z.object({
  assessment: z.object({
    id: z.guid(),
    status: z.enum(['draft', 'in_progress', 'completed', 'cancelled']),
    started_at: z.string().datetime().nullable(),
    completed_at: z.string().datetime().nullable(),
    audit_type: z.enum(['internal', 'external', 'certification']).nullable(),
    audit_scope: z.string().nullable(),
  }),
  site: z.object({
    id: z.guid(),
    name: z.string(),
    address: z.string().nullable(),
  }),
  client: z.object({
    id: z.guid(),
    name: z.string(),
  }),
  auditor: z.object({
    id: z.guid(),
    email: z.string().email(),
  }),
  findings_summary: z.object({
    by_severity: z.object({
      critical: z.number().int().nonnegative(),
      high: z.number().int().nonnegative(),
      medium: z.number().int().nonnegative(),
      low: z.number().int().nonnegative(),
    }),
    by_classification: z.object({
      nc_major: z.number().int().nonnegative(),
      nc_minor: z.number().int().nonnegative(),
      observation: z.number().int().nonnegative(),
      ofi: z.number().int().nonnegative(),
    }),
    by_status: z.object({
      open: z.number().int().nonnegative(),
      in_progress: z.number().int().nonnegative(),
      resolved: z.number().int().nonnegative(),
      closed: z.number().int().nonnegative(),
      dismissed: z.number().int().nonnegative(),
    }),
    total: z.number().int().nonnegative(),
  }),
  /** Top 5 hallazgos por severidad DESC, created_at ASC. */
  top_findings: z
    .array(
      z.object({
        id: z.guid(),
        title: z.string(),
        severity: z.enum(['critical', 'high', 'medium', 'low']),
        classification: z.enum(CLASSIFICATION_VALUES),
        status: z.enum(FINDING_STATUS_VALUES),
        due_date: z.string().datetime().nullable(),
      }),
    )
    .max(5),
  evidence_summary: z.object({
    photos_count: z.number().int().nonnegative(),
    finding_evidence_count: z.number().int().nonnegative(),
  }),
  /**
   * Sprint Y #6b — Hallazgos agrupados por dominio del scoring.
   * Vacío si la plantilla no declara dominios o el assessment no tiene findings.
   */
  findings_by_domain: z.array(
    z.object({
      domain_id: z.string(),
      domain_name: z.string(),
      severity_counts: z.object({
        critical: z.number().int().nonnegative(),
        high: z.number().int().nonnegative(),
        medium: z.number().int().nonnegative(),
        low: z.number().int().nonnegative(),
      }),
      total: z.number().int().nonnegative(),
    }),
  ),
  /**
   * Sprint Y #6b — Porcentaje de cumplimiento por dominio del template.
   * Vacío si la plantilla no declara dominios o no hay datos de scoring.
   */
  compliance_by_requirement: z.array(
    z.object({
      requirement_id: z.string(),
      requirement_label: z.string(),
      status: z.enum(['compliant', 'partial', 'non_compliant', 'not_applicable']),
      percent: z.number().min(0).max(100),
    }),
  ),
});

export type AssessmentSummary = z.infer<typeof AssessmentSummarySchema>;
